/**
 * Per-zip ALL-IN recompute for the Dynamic Pricing Injection Service (WAT-11203).
 *
 * The RSN (regional-sports) fee is per-ZIPCODE and only known on the CLIENT, but
 * `[<PRODUCTTYPE_PKG_OFFER<n>ALLIN>]` is resolved at page-adapter time, so the
 * SSR string carries the DEFAULT-zip RSN. This module is the (isomorphic) contract
 * that lets the client correct it:
 *
 *   server → `pricingData.dpAllIn = { rsn: 13.99, tiers: [{ allIn: "88.98", base: 74.99 }] }`
 *   client → `applyLiveRsnToAllIn(copy, dpAllIn, "7.99")` → "…$82.98/mo…"
 *
 * WHY A DATA CONTRACT AND NOT A DOM MARKER: the first attempt (WAT-11586,
 * `data-dp-allin` / `data-dp-base` spans + `useDynamicRsn`) never worked live —
 * fusion's `DtvText` renderer runs a custom HTML parser that REJECTS any tag
 * carrying custom attributes ("Invalid tag: spandata-dp-allin…"), so no
 * author-side marker survives into the painted DOM. Substituting the NUMBER in
 * the string BEFORE it reaches `DtvText` sidesteps the parser entirely — the same
 * thing the legacy `[first_req_price]` hydration has always done
 * (`utils/reqPriceHydration.ts`).
 *
 * `base` is RSN-EXCLUDED (`OFFER<n>REQPRICE` = net + device fee), so the live
 * all-in is a plain `base + liveRsn`. A package with UNKNOWN RSN (satellite-only
 * MÁS LATINO / MÁS ULTRA / LO MÁXIMO, no DTVI twin to reuse a fee from) gets NO
 * `dpAllIn`: there is nothing safe to recompute. A package EPOCH marks RSN-exempt
 * (ENTERTAINMENT / ÓPTIMO MÁS, `rsnFeeDetails.visible === false`) also gets NO
 * `dpAllIn` — but for the opposite reason: its painted all-in is already correct
 * at every zip, so there is nothing to recompute either. Within `buildDpAllIn`
 * both return null. They differ in whether a gap is reported (`reportUnknownRsn`)
 * and, upstream, in whether `OFFER<n>ALLIN` painted at all — exempt paints
 * `base + 0`; unknown self-suppresses to the author's `||` fallback.
 */

import { toNumber } from "./price";

/**
 * One rung of the offer ladder ("$30 off 1st mo." / "$10 off mos 2-24" / …).
 *
 * `readonly` throughout: this value is attached ONCE per component by
 * `attachPlanCardRsnData` (which is itself careful to clone-on-write) and then read
 * on every render by potentially several `LiveAllInText` leaves sharing the same
 * object reference. An in-place mutation by one future consumer would silently
 * corrupt what every other reader on the card sees, and the symptom would be a wrong
 * price rather than a crash. Make that a compile error instead. (WAT-11203.)
 */
export interface DpAllInTier {
  /** The all-in EXACTLY as the server painted it, default-zip RSN included ("88.98"). */
  readonly allIn: string;
  /** RSN-EXCLUDED base for the same rung (`OFFER<n>REQPRICE`), e.g. 74.99. */
  readonly base: number;
}

/** Everything the client needs to re-price a card's all-ins for the live zip. */
export interface DpAllInData {
  /** The default-zip RSN the server baked into every `allIn` above. */
  readonly rsn: number;
  readonly tiers: readonly DpAllInTier[];
}

/** Prices agree to the cent; anything closer than half a cent is the same money. */
const CENT = 0.005;

/**
 * Escape a value for literal use inside a RegExp (the price strings we build are
 * numeric, so this only really guards the `.` — but never hand-roll that).
 */
const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Rewrite every money literal in `text` in ONE pass, from a `from → to` map.
 *
 * The single pass is a CORRECTNESS requirement, not an optimisation: replacing
 * rung by rung makes each rung re-read what the previous one just wrote, and two
 * rungs of the same ladder can collide — rung 1's LIVE all-in equals rung 2's
 * BAKED all-in whenever their bases differ by exactly (live − baked). With bases
 * $10 apart, 13.99 baked and the 23.99 cap zip that is exactly what happens, and
 * the second pass rewrote rung 1 again, painting both rungs the same wrong price.
 *
 * MATCHES ARE ANCHORED TO A CURRENCY CONTEXT. Keying only on the rendered number
 * would let an unrelated literal that happens to equal an all-in — a channel count,
 * a phone fragment, a data cap, another product's price — be silently repriced in
 * the same block of regulated copy. `OFFER<n>ALLIN` resolves to a bare 2-dp number
 * BY CONTRACT (`buildOfferLadder`: "the CMS supplies the `$`"), so every painted
 * all-in is preceded by an authored `$`. Requiring that `$` — optionally separated
 * by whitespace or one markup tag, since authors wrap the number
 * (`$<span …>88.98</span>`) — makes a non-price collision unreachable while
 * matching every shape the grammar actually paints.
 *
 * The `$` anchor also subsumes the old digit-boundary guard on the left: "88.98"
 * cannot match inside "$188.98" because the character after the `$` is "1". The
 * trailing `(?![\d])` still keeps it out of "$88.985" — deliberately NOT a
 * lookbehind, which older Safari throws on at RegExp construction. Longest literal
 * first, so a shorter one can never claim the prefix of a longer one. All
 * occurrences are replaced: the same all-in commonly appears in both price copy and
 * card legal.
 */
/** `$`, then optional whitespace and/or a single markup tag, e.g. `$` / `$<span …>`. */
const CURRENCY_LEAD = "\\$(?:\\s|<[^<>]*>)*";

function replaceMoneyLiterals(text: string, replacements: Map<string, string>): string {
  const alternation = Array.from(replacements.keys())
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");
  const re = new RegExp(`(${CURRENCY_LEAD})(${alternation})(?![\\d])`, "g");
  return text.replace(re, (match, lead, hit) => {
    const to = replacements.get(hit);
    return to === undefined ? match : `${lead}${to}`;
  });
}

/**
 * Rewrite every all-in in `content` from the server's default-zip RSN to `rsnFee`.
 *
 * Returns `content` UNCHANGED (same reference) whenever there is nothing to do —
 * no metadata, unusable fee, or the live zip resolves to the RSN already baked in.
 * That identity return is what lets a caller memo on it and skip a re-render.
 * Never throws: a malformed tier is skipped, not fatal.
 */
export function applyLiveRsnToAllIn(
  content: string | null | undefined,
  dpAllIn: DpAllInData | null | undefined,
  rsnFee: string | number | null | undefined
): string {
  const text = content ?? "";
  const tiers = dpAllIn?.tiers;
  if (!text || !tiers?.length) {
    return text;
  }
  const live = toNumber(rsnFee);
  const baked = toNumber(dpAllIn?.rsn);
  // A negative / non-numeric fee is a bad read, not a zip with no RSN — leave the
  // server value alone rather than paint a wrong price.
  if (live === null || live < 0 || baked === null) {
    return text;
  }
  if (Math.abs(live - baked) < CENT) {
    return text; // same RSN as SSR (default zip) → the painted price is already right
  }
  // Collect every rung's rewrite FIRST, then substitute once (see
  // `replaceMoneyLiterals`). A duplicate `allIn` across rungs is the same money —
  // one baked RSN means one base behind it — so the first mapping wins.
  const replacements = new Map<string, string>();
  tiers.forEach(tier => {
    const base = toNumber(tier?.base);
    const from = typeof tier?.allIn === "string" ? tier.allIn.trim() : "";
    if (base === null || !from || replacements.has(from)) {
      return; // malformed tier — skipped, never fatal
    }
    const to = (base + live).toFixed(2);
    if (to !== from) {
      replacements.set(from, to);
    }
  });
  return replacements.size ? replaceMoneyLiterals(text, replacements) : text;
}

export default applyLiveRsnToAllIn;
